import React, { useContext, useState, useEffect, useRef } from 'react';
import styles from '../DocumentUploadPage/DocumentUploadPage.module.scss';
import { BeneDocAdditionalContext } from 'src/shared/providers/DocumentUploadContextProvider';
import { useGetCasePassports } from 'src/shared/queries/useGetCasePassports';
import { AsyncDataHandler } from '@ds/elements/AsyncDataHandler/AsyncDataHandler';
import CompletePassport from '../PassportGroup/CompletePassport';
import PassportUploadArea from '../PassportUploadArea/PassportUploadArea';
import { IPassportGroup } from 'src/shared/types/Documents';
import PassportGroup from '../PassportGroup/PassportGroup';
import { useInterceptor } from '../PassportClickInterceptor/PassportClickContext';
import { useGetCaseDetails } from 'src/shared/queries/useGetCaseDetails';

const AllCasePassportsHeader: React.FC = () => {
  const TITLES = {
    title: 'Passports',
    description: 'Your immigration service provider will advise on the validity and blank page requirements.',
  };
  return (
    <>
      <p className={styles['title']}>{TITLES.title}</p>
      <div className={styles['passport-description-container']}>
        <p>{TITLES.description}</p>
      </div>
    </>
  );
};

const AllCasePassports: React.FC = () => {
  const { beneId, caseId, hasPassportItem } = useContext(BeneDocAdditionalContext);
  const { data: caseDetails } = useGetCaseDetails(caseId ?? 0);
  const isCaseActive = caseDetails?.isActive ?? false;
  const {
    data: passports,
    isLoading: isLoadingPassports,
    refetch,
  } = beneId && caseId ? useGetCasePassports(beneId, caseId) : { data: null };
  const [passportsToUpload, setPassportsToUpload] = useState<IPassportGroup[]>([]);

  const caseDocsRef = useRef<HTMLDivElement>(null);
  const { setEnabled } = useInterceptor();

  useEffect(() => {
    if (passportsToUpload.length > 0) {
      setEnabled(true);
    } else {
      setEnabled(false);
    }
  }, [passportsToUpload.length]);

  const addNewPassport = () => {
    const newPassportGroup: IPassportGroup = {
      fileList: [],
    };
    setPassportsToUpload((prev) => [...prev, newPassportGroup]);
  };

  const deletePassportGroup = (index: number) => {
    setPassportsToUpload((prev) => prev.filter((_, i) => i !== index));
  };

  // Remove file at groupIndex and fileIndex
  const handleDeleteAllCasePassportsFile = (groupIndex: number, fileIndex: number) => {
    setPassportsToUpload((prev) =>
      prev.map((group, idx) =>
        idx === groupIndex ? { ...group, fileList: group.fileList.filter((_, fIdx) => fIdx !== fileIndex) } : group
      )
    );
  };

  return (
    <div className={styles['case-docs-page']} ref={caseDocsRef} data-leave-modal-content>
      <AllCasePassportsHeader />
      <AsyncDataHandler isLoading={isLoadingPassports ?? false}>
        {!hasPassportItem && (
          <>
            {passportsToUpload?.map((passport, index) => (
              <div className={styles['documents-grid-container']} key={index}>
                <PassportGroup
                  allCasePassports
                  fileList={passport.fileList}
                  onDeleteAllCasePassportsGroup={deletePassportGroup}
                  addFileAllCasePassports={(file) =>
                    setPassportsToUpload((prev) =>
                      prev.map((p, i) =>
                        i === index ? { ...p, fileList: [...p.fileList, ...(Array.isArray(file) ? file : [file])] } : p
                      )
                    )
                  }
                  onRefreshChecklist={refetch}
                  index={index}
                  onDeleteAllCasePassportsFile={handleDeleteAllCasePassportsFile}
                />
              </div>
            ))}
            {isCaseActive && (
              <PassportUploadArea
                allowFromCobalt
                addChecklistItemGroupForPassport={addNewPassport}
                onRefreshChecklist={refetch}
                allCasePassports
                allCasePassportIds={passports?.map((passport) => passport?.passportId) ?? []}
              />
            )}
          </>
        )}
        {passports?.map((passport, index) => (
          <div key={index} className={styles['documents-grid-container']}>
            <CompletePassport passportInfo={passport} allCasePassports />
          </div>
        ))}
      </AsyncDataHandler>
    </div>
  );
};

export default AllCasePassports;
